'use client';

import React, { useState } from 'react';

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    /** Custom fallback content shown when the image fails to load */
    fallback?: React.ReactNode;
}

function DefaultFallback({ className }: { className?: string }) {
    return (
        <div
            className={`flex flex-col items-center justify-center gap-2 bg-bg-surface border border-border-subtle rounded-lg text-zinc-500 ${className ?? ''}`}
            style={{ minHeight: 80 }}
        >
            <svg
                className="w-8 h-8 text-zinc-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"
                />
            </svg>
            <span className="text-xs">Image unavailable</span>
        </div>
    );
}

export function ImageWithFallback({
    fallback,
    onError,
    className,
    alt,
    ...props
}: ImageWithFallbackProps) {
    const [hasError, setHasError] = useState(false);

    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setHasError(true);
        onError?.(e);
    };

    if (hasError) {
        return fallback ? <>{fallback}</> : <DefaultFallback className={className} />;
    }

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            className={className}
            alt={alt ?? ''}
            onError={handleError}
            {...props}
        />
    );
}
